import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  PDFDownloadLink,
} from '@react-pdf/renderer';
import {
  Button,
} from 'antd';
import {
  DownloadOutlined,
  FilePdfOutlined,
} from '@ant-design/icons';



// ─────────────────────────────────────────────
// UTILIDADES
// ─────────────────────────────────────────────
const fmt = (n) =>
  new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
  }).format(n);

const fmtNum = (n) => new Intl.NumberFormat('es-CO').format(n);

// ─────────────────────────────────────────────
// COLORES PDF
// ─────────────────────────────────────────────
const C = {
  navy:       '#1e3a5f',   // header azul oscuro (Total Productos, Valor Entrante, Valor Entregado)
  green:      '#1e6b3a',   // header verde (Saldo por Ejecutar)
  labelBg:    '#dce6f0',   // fondo fila etiquetas (azul)
  labelBgGrn: '#c8e6c9',   // fondo fila etiquetas (verde)
  valueBg:    '#ffffff',
  border:     '#b0bec5',
  textDark:   '#1a2a3a',
  textGray:   '#546e7a',
  white:      '#ffffff',
  rowAlt:     '#f5f8fb',
  totalBg:    '#dce6f0',
};

const p = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 8,
    paddingTop: 28,
    paddingBottom: 36,
    paddingHorizontal: 28,
    backgroundColor: C.white,
    color: C.textDark,
  },

  // ── Page header ──
  pageHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 14,
    paddingBottom: 8,
    borderBottomWidth: 1.5,
    borderBottomColor: C.navy,
  },
  pageTitle: { fontSize: 13, fontFamily: 'Helvetica-Bold', color: C.navy },
  pageSubtitle: { fontSize: 7, color: C.textGray, marginTop: 2 },

  // ── Section block ──
  section: { marginBottom: 8 },

  // Header bar (colored)
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 5,
    paddingHorizontal: 8,
  },
  sectionHeaderSquare: {
    width: 8,
    height: 8,
    backgroundColor: C.white,
    marginRight: 6,
    opacity: 0.8,
  },
  sectionHeaderText: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 8,
    color: C.white,
    letterSpacing: 0.3,
  },

  // Label row
  labelRow: {
    flexDirection: 'row',
    paddingVertical: 4,
    borderBottomWidth: 1,
    borderBottomColor: C.border,
  },
  labelCell: {
    flex: 1,
    textAlign: 'center',
    fontSize: 7,
    color: C.textGray,
    borderRightWidth: 1,
    borderRightColor: C.border,
    paddingHorizontal: 4,
  },
  labelCellLast: {
    flex: 1,
    textAlign: 'center',
    fontSize: 7,
    color: C.textGray,
    paddingHorizontal: 4,
  },

  // Value row
  valueRow: {
    flexDirection: 'row',
    paddingVertical: 7,
    backgroundColor: C.valueBg,
    borderBottomWidth: 1,
    borderBottomColor: C.border,
  },
  valueCell: {
    flex: 1,
    textAlign: 'center',
    fontFamily: 'Helvetica-Bold',
    fontSize: 9,
    color: C.textDark,
    borderRightWidth: 1,
    borderRightColor: C.border,
    paddingHorizontal: 4,
  },
  valueCellLast: {
    flex: 1,
    textAlign: 'center',
    fontFamily: 'Helvetica-Bold',
    fontSize: 9,
    color: C.textDark,
    paddingHorizontal: 4,
  },
  valueCellGreen: {
    flex: 1,
    textAlign: 'center',
    fontFamily: 'Helvetica-Bold',
    fontSize: 9,
    color: '#1b5e20',
    borderRightWidth: 1,
    borderRightColor: C.border,
    paddingHorizontal: 4,
  },
  valueCellGreenLast: {
    flex: 1,
    textAlign: 'center',
    fontFamily: 'Helvetica-Bold',
    fontSize: 9,
    color: '#1b5e20',
    paddingHorizontal: 4,
  },

  // Outer border wrap
  sectionBorder: {
    borderWidth: 1,
    borderColor: C.border,
  },

  // ── PO Table ──
  tableColHeader: {
    flexDirection: 'row',
    backgroundColor: C.navy,
    paddingVertical: 5,
    paddingHorizontal: 4,
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 4,
    paddingHorizontal: 4,
    borderBottomWidth: 1,
    borderBottomColor: C.border,
    backgroundColor: C.white,
  },
  tableRowAlt: { backgroundColor: C.rowAlt },
  tableTotalRow: {
    flexDirection: 'row',
    paddingVertical: 5,
    paddingHorizontal: 4,
    backgroundColor: C.totalBg,
    borderTopWidth: 1,
    borderTopColor: C.navy,
  },
  th: { fontFamily: 'Helvetica-Bold', fontSize: 7, color: C.white },
  td: { fontSize: 7.5, color: C.textDark },
  tdB: { fontFamily: 'Helvetica-Bold', fontSize: 7.5, color: C.textDark },
  cPo:   { width: '12%' },
  cCant: { width: '10%', textAlign: 'right' },
  cMon:  { width: '19%', textAlign: 'right' },
  cEnt:  { width: '22%', textAlign: 'right' },

  // ── Footer ──
  footer: {
    position: 'absolute',
    bottom: 14,
    left: 28,
    right: 28,
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderTopWidth: 0.5,
    borderTopColor: C.border,
    paddingTop: 4,
  },
  footerText: { fontSize: 6.5, color: C.textGray },

  note: {
    marginTop: 5,
    paddingVertical: 4,
    paddingHorizontal: 6,
    backgroundColor: '#fffde7',
    borderLeftWidth: 2,
    borderLeftColor: '#f9a825',
  },
  noteText: { fontSize: 6.5, color: '#5d4037' },
});

// ── Helper: renders one section block (header + label row + value row) ──
const PdfSection = ({ title, headerColor = C.navy, labelBg = C.labelBg, cols }) => (
  <View style={[p.section, p.sectionBorder]}>
    {/* colored header */}
    <View style={[p.sectionHeader, { backgroundColor: headerColor }]}>
      <View style={p.sectionHeaderSquare} />
      <Text style={p.sectionHeaderText}>{title}</Text>
    </View>
    {/* label row */}
    <View style={[p.labelRow, { backgroundColor: labelBg }]}>
      {cols.map((c, i) =>
        i < cols.length - 1
          ? <Text key={i} style={p.labelCell}>{c.label}</Text>
          : <Text key={i} style={p.labelCellLast}>{c.label}</Text>
      )}
    </View>
    {/* value row */}
    <View style={p.valueRow}>
      {cols.map((c, i) => {
        const isLast = i === cols.length - 1;
        const style = c.green
          ? (isLast ? p.valueCellGreenLast : p.valueCellGreen)
          : (isLast ? p.valueCellLast : p.valueCell);
        return <Text key={i} style={style}>{c.value}</Text>;
      })}
    </View>
  </View>
);

// ─────────────────────────────────────────────
// DOCUMENTO PDF
// ─────────────────────────────────────────────
const InformePDF = ({ data }) => {
  const totales = data.ordenes.reduce(
    (acc, o) => ({
      cantidad: acc.cantidad + o.cantidad,
      sinIva:   acc.sinIva   + o.totalSinIva,
      conIva:   acc.conIva   + o.totalConIva,
      iva:      acc.iva      + o.iva,
      entregado:acc.entregado+ o.totalEntregadoConIva,
    }),
    { cantidad: 0, sinIva: 0, conIva: 0, iva: 0, entregado: 0 }
  );

  return (
    <Document title="Informe Órdenes de Compra" author="Sistema de Gestión">
      <Page size="A4" style={p.page}>

        {/* ── PAGE HEADER ── */}
        <View style={p.pageHeader}>
          <View>
            <Text style={p.pageTitle}>Informe de Órdenes de Compra</Text>
            <Text style={p.pageSubtitle}>Generado el {data.generadoEl}</Text>
          </View>
        </View>

        {/* ── TOTAL PRODUCTOS ── */}
        <PdfSection
          title="Total Productos"
          cols={[
            { label: 'Total Órdenes',         value: String(data.totalOrdenes) },
            { label: 'Productos Entrantes',    value: fmtNum(data.totalProductosEntrantes) },
            { label: 'Productos Entregados',   value: fmtNum(data.totalProductosEntregados) },
            { label: 'Avance Promedio',        value: `${data.avancePromedio} %` },
          ]}
        />

        {/* ── VALOR ENTRANTE ── */}
        <PdfSection
          title="Valor Entrante"
          cols={[
            { label: 'Sin IVA',    value: fmt(data.valorEntrante.sinIva) },
            { label: 'Con IVA',    value: fmt(data.valorEntrante.conIva) },
            { label: 'Valor IVA',  value: fmt(data.valorEntrante.iva) },
          ]}
        />

        {/* ── VALOR ENTREGADO ── */}
        <PdfSection
          title="Valor Entregado"
          cols={[
            { label: 'Sin IVA',           value: fmt(data.valorEntregado.sinIva) },
            { label: 'Con IVA',           value: fmt(data.valorEntregado.conIva) },
            { label: 'Valor IVA',         value: fmt(data.valorEntregado.iva) },
            { label: '% Valor Entregado', value: `${data.valorEntregado.porcentaje} %` },
          ]}
        />

        {/* ── SALDO POR EJECUTAR ── */}
        <PdfSection
          title="Saldo por Ejecutar (Entrante − Entregado)"
          headerColor={C.green}
          labelBg={C.labelBgGrn}
          cols={[
            { label: 'Sin IVA',          value: fmt(data.saldoPorEjecutar.sinIva),   green: true },
            { label: 'Con IVA',          value: fmt(data.saldoPorEjecutar.conIva),   green: true },
            { label: '% Saldo Pendiente',value: `${data.saldoPorEjecutar.porcentajePendiente} %`, green: true },
          ]}
        />

        {/* ── PLANES DE COMPRA ── */}
        <PdfSection
          title="Valor Total Planes de Compra (Presupuestado)"
          cols={[
            { label: 'Presupuestado',        value: fmt(data.planesCompra.presupuestado) },
            { label: 'Valor Ejecutado Con IVA', value: fmt(data.planesCompra.ejecutadoConIva) },
            { label: 'Diferencia',           value: fmt(data.planesCompra.diferencia) },
          ]}
        />

        {/* ── CONSOLIDADO POR PO ── */}
        <View style={[p.section, p.sectionBorder]}>
          {/* header */}
          <View style={[p.sectionHeader, { backgroundColor: C.navy }]}>
            <View style={p.sectionHeaderSquare} />
            <Text style={p.sectionHeaderText}>Consolidado por Orden de Compra (PO)</Text>
          </View>
          {/* col headers */}
          <View style={p.tableColHeader}>
            <Text style={[p.th, p.cPo]}>PO</Text>
            <Text style={[p.th, p.cCant]}>Cantidad</Text>
            <Text style={[p.th, p.cMon]}>Total Sin IVA</Text>
            <Text style={[p.th, p.cMon]}>Total Con IVA</Text>
            <Text style={[p.th, p.cMon]}>IVA Total</Text>
            <Text style={[p.th, p.cEnt]}>Entregado Con IVA</Text>
          </View>
          {/* data rows */}
          {data.ordenes.map((o, i) => (
            <View key={o.po} style={[p.tableRow, i % 2 === 1 && p.tableRowAlt]}>
              <Text style={[p.tdB, p.cPo]}>{o.po}</Text>
              <Text style={[p.td,  p.cCant]}>{fmtNum(o.cantidad)}</Text>
              <Text style={[p.td,  p.cMon]}>{fmt(o.totalSinIva)}</Text>
              <Text style={[p.td,  p.cMon]}>{fmt(o.totalConIva)}</Text>
              <Text style={[p.td,  p.cMon]}>{fmt(o.iva)}</Text>
              <Text style={[p.td,  p.cEnt]}>{fmt(o.totalEntregadoConIva)} ({o.porcentajeEntregado}%)</Text>
            </View>
          ))}
          {/* total row */}
          <View style={p.tableTotalRow}>
            <Text style={[p.tdB, p.cPo]}>TOTAL</Text>
            <Text style={[p.tdB, p.cCant]}>{fmtNum(totales.cantidad)}</Text>
            <Text style={[p.tdB, p.cMon]}>{fmt(totales.sinIva)}</Text>
            <Text style={[p.tdB, p.cMon]}>{fmt(totales.conIva)}</Text>
            <Text style={[p.tdB, p.cMon]}>{fmt(totales.iva)}</Text>
            <Text style={[p.tdB, p.cEnt]}>{fmt(totales.entregado)}</Text>
          </View>
        </View>



        {/* ── FOOTER ── */}
        <View style={p.footer} fixed>
          <Text style={p.footerText}>Informe de Órdenes de Compra — Confidencial</Text>
          <Text
            style={p.footerText}
            render={({ pageNumber, totalPages }) => `Página ${pageNumber} de ${totalPages}`}
          />
        </View>

      </Page>
    </Document>
  );
};

// ─────────────────────────────────────────────
// COMPONENTE PRINCIPAL (UI Ant Design)
// ─────────────────────────────────────────────
const InformeOrdenesCompraPDF = ({ data }) => {
  const reportData = data || {};

  return (
    <PDFDownloadLink
      document={<InformePDF data={reportData} />}
      fileName={`informe_ordenes_compra_${reportData.generadoEl.replace(
        /[/ :]/g,
        "-"
      )}.pdf`}
    >
      {({ loading }) => (
        <Button
          danger
          type="primary"
          icon={<FilePdfOutlined />}
          loading={loading}
        >
          {loading ? "Generando PDF..." : "Exportar PDF"}
        </Button>
      )}
    </PDFDownloadLink>
  );
};

export default InformeOrdenesCompraPDF;